// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import GridContainer from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Select from "@insite/mobius/Select";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { CustomAppState } from "@pacific/App/Store/Reducers";
import LazyImage, { LazyImageProps } from "@pacific/MobiusOverrides/LazyImage/LazyImage";
import { color, xScreen } from "@pacific/XDL/Utilities";

import * as React from "react";
import { css } from "styled-components";

const enum fields {
    teamMembers = "teamMembers",
}

const enum teamMembersFields {
    name = "name",
    image = "image",
    branch = "branch",
    jobTitle = "jobTitle",
    description = "description",
}

interface TeamMember {
    fields: {
        name: string;
        image: string;
        branch: string;
        jobTitle: string;
        description: string;
    };
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.teamMembers]: TeamMember[];
    };
}

type Props = OwnProps;

export interface OurTeamStyles {
    fitlerWrapper?: InjectableCss;
    memberCardGridItem?: GridItemProps;
    memberCard?: InjectableCss;
    memberCardInfo?: InjectableCss;
    image?: LazyImageProps;
    name?: TypographyProps;
    jobTitle?: TypographyProps;
    description?: TypographyProps;
    memberDetails?: InjectableCss;
    detailsImage?: LazyImageProps;
    memberDetailsInfo?: InjectableCss;
}

export const ourTeamStyles: OurTeamStyles = {
    fitlerWrapper: {
        css: css`
            margin-bottom: 32px;
            max-width: 260px;
        `,
    },
    memberCardGridItem: {
        width: [12, 12, 3, 2, 2],
        css: css`
            ${xScreen("lt", "md")} {
                padding-left: 0;
                padding-right: 0;
            }
        `,
    },
    memberCard: {
        css: css`
            display: flex;
            cursor: pointer;
            flex-direction: column;
            align-items: center;
            text-align: center;
            padding: 0 4px 20px 4px;
            height: 100%;
            width: 100%;

            ${xScreen("lt", "md")} {
                flex-direction: row;
                justify-content: flex-start;
                flex-wrap: wrap;
                padding: 0 11px 20px 11px;
            }

            ${({ isActive }: any) => {
                return isActive
                    ? css`
                          background: linear-gradient(
                              to bottom,
                              transparent 0,
                              transparent 20px,
                              ${color("common.ui8")} 20px,
                              ${color("common.ui8")} 100%
                          );

                          h5,
                          p {
                              color: ${color("common.white")};
                          }
                      `
                    : css`
                          background: linear-gradient(
                              to bottom,
                              transparent 0,
                              transparent 20px,
                              ${color("common.ui2")} 20px,
                              ${color("common.ui2")} 100%
                          );
                      `;
            }}
        `,
    },
    memberCardInfo: {
        css: css``,
    },
    image: {
        transformations: {
            mobileWidth: 152,
            width: 166,
        },
        css: css`
            object-fit: cover;
            width: 166px;
            height: 166px;
            margin-bottom: 20px;

            ${xScreen("lt", "md")} {
                margin-right: 24px;
                margin-bottom: 12px;
                height: 152px;
                width: 152px;
            }
        `,
    },
    name: {
        variant: "h5",
        css: css`
            margin-bottom: 4px;
            text-align: center;
        `,
    },
    jobTitle: {
        variant: "p",
        css: css`
            margin-bottom: 0;
            text-align: center;
        `,
    },
    description: {
        variant: "p",
        css: css`
            margin-bottom: 0;
        `,
    },
    memberDetails: {
        css: css`
            border-top: 1px solid ${color("common.border")};
            border-bottom: 1px solid ${color("common.border")};
            padding: 20px 0;
            display: flex;
            align-items: center;
            width: 100%;
        `,
    },
    detailsImage: {
        transformations: {
            width: 306,
        },
        css: css`
            object-fit: cover;
            width: 306px;
            height: 306px;
            flex: 0 0 306px;
        `,
    },
    memberDetailsInfo: {
        css: css`
            margin-left: 32px;
            h5,
            p {
                text-align: left;
            }
        `,
    },
};

const styles = ourTeamStyles;

export const OurTeam: React.FC<Props> = ({ fields }) => {
    const [activeMember, setActiveMember] = React.useState<number | undefined>();
    const [branches, setBranches] = React.useState<string[]>([]);
    const [selectedBranch, setSelectedBranch] = React.useState<string | undefined>();
    const { isTabletScreen, isMobileScreen } = useAppSelector(state => ({
        isTabletScreen: (state as CustomAppState).xCommon.isTabletScreen,
        isMobileScreen: (state as CustomAppState).xCommon.isMobileScreen,
    }));
    const itemsPerRow = isTabletScreen ? 4 : 6;

    React.useEffect(() => {
        const branches = [...new Set(fields.teamMembers.map((member: TeamMember) => member.fields.branch))];
        setBranches(branches);
    }, [fields.teamMembers]);

    const handleBranchChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedBranch(event.currentTarget.value);
        setActiveMember(undefined);
    };

    const onTeamMemberClick = (index: number) => {
        activeMember === index ? setActiveMember(undefined) : setActiveMember(index);
    };
    
    return (
        <>
            <StyledWrapper {...styles.fitlerWrapper}>
                <Select
                    value={selectedBranch}
                    onChange={handleBranchChange}
                    data-id="filterByRegion"
                    label={translate("Filter by Region")}
                >
                    <option value="">{translate("Select Option")}</option>
                    {branches.map(branch => (
                        <option key={branch} value={branch}>
                            {branch}
                        </option>
                    ))}
                </Select>
            </StyledWrapper>
            <GridContainer gap={22}>
                {fields.teamMembers.map((member: TeamMember, index) => {
                    if (!selectedBranch || member.fields.branch === selectedBranch) {
                        return (
                            <GridItem
                                key={member.fields.name}
                                onClick={() => onTeamMemberClick(index)}
                                {...styles.memberCardGridItem}
                                style={{ order: index }}
                            >
                                <StyledWrapper {...styles.memberCard} {...{ isActive: index === activeMember }}>
                                    <LazyImage
                                        src={member.fields.image}
                                        altText={member.fields.name}
                                        {...styles.image}
                                    />
                                    <StyledWrapper {...styles.memberCardInfo}>
                                        <Typography {...styles.name}>{member.fields.name}</Typography>
                                        <Typography {...styles.jobTitle}>{member.fields.jobTitle}</Typography>
                                    </StyledWrapper>

                                    {isMobileScreen && (
                                        <Typography {...styles.description}>{member.fields.description}</Typography>
                                    )}
                                </StyledWrapper>
                            </GridItem>
                        );
                    }
                    return null;
                })}
                {!isMobileScreen && activeMember !== undefined && (
                    <GridItem
                        width={12}
                        style={{ order: activeMember + (itemsPerRow - (activeMember % itemsPerRow)) - 1 }}
                    >
                        <StyledWrapper {...styles.memberDetails}>
                            <LazyImage
                                src={fields.teamMembers[activeMember].fields.image}
                                altText={fields.teamMembers[activeMember].fields.name}
                                {...styles.detailsImage}
                            />
                            <StyledWrapper {...styles.memberDetailsInfo}>
                                <Typography {...styles.name}>{fields.teamMembers[activeMember].fields.name}</Typography>
                                <Typography {...styles.jobTitle}>
                                    {fields.teamMembers[activeMember].fields.jobTitle}
                                </Typography>
                                <Typography {...styles.description} style={{ marginTop: "20px" }}>
                                    {fields.teamMembers[activeMember].fields.description}
                                </Typography>
                            </StyledWrapper>
                        </StyledWrapper>
                    </GridItem>
                )}
            </GridContainer>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: OurTeam,
    definition: {
        group: "Xngage Custom" as any,
        fieldDefinitions: [
            {
                name: fields.teamMembers,
                editorTemplate: "ListField",
                defaultValue: [],
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: teamMembersFields.name,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: teamMembersFields.image,
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: teamMembersFields.jobTitle,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: teamMembersFields.description,
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: teamMembersFields.branch,
                        editorTemplate: "SystemListDropDownField",
                        systemListName: "OurTeamBranches",
                        defaultValue: "",
                        isRequired: true,
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
